import { memo } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import classNames from "classnames";
import AirlineLogo from "app/pages/.shared/AirlineLogo";
import { convertDurationLabel } from "app/pages/.shared/ItineraryUtils/ItineraryUtils";
import { ITINERARY_INFO_TYPE } from "app/constants";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { CABINS } from "app/constants";
import { getCabinLabelMessageId } from "app/utils/i18nUtils";
import StopoverFlightDurationLabel from "app/pages/Stopover/StopoverFlightDurationLabel/StopoverFlightDurationLabel";
import className from "classnames";

import "./ItineraryView.scss";

const ItineraryView = ({ label, flightData = [], cabin, showDetails = false }) => {
	//console.log("flightData ", flightData);
	const timelineComponentRender = event => {
		const { type, data } = event;
		const flightInfoNode = (
			<div
				className={classNames("itinerary-timeline__infos", {
					"itinerary-timeline__infos-flight-direct": data.isDirect,
				})}
			>
				<div className="itinerary-timeline__flight-duration">
					<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
						{convertDurationLabel(data.duration)}
					</Typography>
				</div>
				{data.legInfo && <div className="itinerary-timeline__leg-info">{data.legInfo}</div>}
				<div className="itinerary-timeline__airline-info">
					<AirlineLogo airlineCode={data.airlineCode} />
					<div className="itinerary-timeline__flight-airline">
						{data.airlineName && (
							<span className="itinerary-timeline__airline-name">
								{data.airlineName}
							</span>
						)}
						{data.flightNumber && (
							<span className="itinerary-timeline__flight-number">
								{data.flightNumber}
							</span>
						)}
						{data.operatingAirlineName && (
							<div className="itinerary-timeline__flight-operator">
								<FormattedMessage
									values={{
										airline: data.operatingAirlineName,
									}}
									id="quotation.flight.flight.operator.label"
								/>
							</div>
						)}
						{cabin && (
							<span className="itinerary-timeline__flight-cabin">
								<FormattedMessage id="general.class.label" />{" "}
								<FormattedMessage id={getCabinLabelMessageId(cabin)} />
							</span>
						)}
					</div>
				</div>
			</div>
		);
		switch (type) {
			case ITINERARY_INFO_TYPE.SCHEDULE:
				return (
					<>
						<div className="itinerary-timeline__schedule">
							<div className="itinerary-timeline__time">
								<div className="take-off-time">{data.time}</div>
							</div>
							<div className={"itinerary-timeline__date"}>
								<p
									className={classNames({
										"itinerary-timeline__next-day":
											data.isNextDay ||
											(data.arrivalDaysCounter &&
												data.arrivalDaysCounter > 0),
									})}
								>
									<FormattedDate
										timeZone="UTC"
										value={new Date(data.date)}
										weekday="short"
										day="2-digit"
										month="short"
									/>
								</p>
							</div>
						</div>
						<div
							className={classNames({
								"itinerary-timeline__node": true,
								"itinerary-timeline__node--stopover": data?.isFlightStopOver,
								"itinerary-timeline__node--stopover--detail": showDetails,
							})}
						/>
						<div className="itinerary-timeline__airport-info">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.LARGE}
								isBold
								data-testid="itinerary-timeline-airport-iata"
								className={classNames("itinerary-timeline__airport-iata", {
									"itinerary-timeline__airport-iata--mismatch": data?.mismatch,
								})}
							>
								{data.iata}
							</Typography>
							{showDetails && (
								<Typography
									variant={TYPOGRAPHY_VARIANTS.SMALL}
									className="itinerary-timeline__airport-name"
								>
									{data.airport}
								</Typography>
							)}
						</div>
					</>
				);
			case ITINERARY_INFO_TYPE.FLIGHT_INFO:
				return (
					<>
						<div className="itinerary-timeline__line" />
						{flightInfoNode}
					</>
				);
			case ITINERARY_INFO_TYPE.FLIGHT_PREVIEW_INFO:
				return (
					<>
						{data.isDirect ? (
							<div className="itinerary-timeline__line" />
						) : (
							<div className="itinerary-timeline__line itinerary-timeline__line--transit" />
						)}
						{flightInfoNode}
					</>
				);
			case ITINERARY_INFO_TYPE.TRANSIT_INFO:
				return (
					<>
						<div className="itinerary-timeline__line--dashed" />
						<div className="itinerary-timeline__infos">
							<div className="itinerary-timeline__transit-detail">
								<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
									<FormattedMessage
										id="general.stop.duration"
										values={{
											value: convertDurationLabel(data.duration),
										}}
									/>
								</Typography>
							</div>
						</div>
					</>
				);
			case ITINERARY_INFO_TYPE.STOPOVER_INFO:
				return (
					<>
						<div className="itinerary-timeline__line--dashed" />
						<div className="itinerary-timeline__infos">
							<div className="itinerary-timeline__transit-detail itinerary-timeline__stopover-detail">
								<Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
									<FormattedMessage
										id="general.stopover.duration"
										values={{
											value: convertDurationLabel(data.duration),
										}}
									/>
								</Typography>
							</div>
						</div>
					</>
				);
			case ITINERARY_INFO_TYPE.STOPOVER_PREVIEW_INFO:
				return (
					<>
						<div className="itinerary-timeline__line itinerary-timeline__line--transit itinerary-timeline__line--transit-stopover" />
						<div className="itinerary-timeline__infos">
							<div className="itinerary-timeline__transit-detail itinerary-timeline__stopover-detail">
								<StopoverFlightDurationLabel
									stopOverDayCount={data.duration}
									stopOverCountry={data.stopOverCity}
								/>
							</div>

							{data.stopCount && (
								<div className="itinerary-timeline__leg-info">{data.stopCount}</div>
							)}
							<AirlineLogo airlineCode={data.airlineCode} />
						</div>
					</>
				);
			default:
				return "";
		}
	};

	return (
		<div className="itinerary-view">
			<div className="itinerary-view__flight-label">
				<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
					<FormattedMessage id={label} />
				</Typography>
			</div>
			{flightData.length > 0 && (
				<div
					className={className({
						"itinerary-timeline": true,
						"itinerary-timeline--flight-indirect": flightData.length > 6,
					})}
					data-testid="itinerary-timeline"
				>
					{flightData.map(event => timelineComponentRender(event))}
				</div>
			)}
		</div>
	);
};

ItineraryView.propTypes = {
	label: PropTypes.string,
	flightData: PropTypes.array,
	cabin: PropTypes.oneOf(Object.values(CABINS)),
	showDetails: PropTypes.bool,
};

export default memo(ItineraryView);
